import React from "react";

import { getPageInstanceId } from "@/main/navigation/CurrentPageInstance";
import {
    HistoryReplaceReason,
    ScrollOffset,
    writeNavigationHistoryState,
} from "@/main/navigation/NavigationHistoryState";

export type { HistoryReplaceReason };

/**
 * The setter's options.
 *
 * `replace` is a REASON, never a boolean (§13.1/§13.3 of the history-ownership design). A member
 * action that changes the address bar must push, so that Back returns to the previous address; the
 * only exemptions are the four cases where the address being overwritten was never a state the member
 * navigated to, and each has to be named. `{ replace: true }` no longer compiles, which is what makes
 * the compiler — rather than a per-page hunt — the thing that enumerates the exemptions.
 */
export type HistoryBackedStateOptions = { replace?: HistoryReplaceReason };

export type HistoryBackedStateAction<T> = React.SetStateAction<T>;
export type HistoryBackedStateSetter<T> = (
    action: HistoryBackedStateAction<T>,
    options?: HistoryBackedStateOptions,
) => void;

type BrowserLocationSnapshot = {
    pathname: string;
    search: string;
    hash: string;
};

function getLocationSnapshot(): BrowserLocationSnapshot {
    return {
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
    };
}

function getCurrentUrl(location: BrowserLocationSnapshot): string {
    return `${location.pathname}${location.search}${location.hash}`;
}

function resolveStateAction<T>(action: HistoryBackedStateAction<T>, previous: T): T {
    return typeof action === "function"
        ? (action as (previous: T) => T)(previous)
        : action;
}

export function useHistoryBackedState<T>({
    initialState,
    readState,
    buildUrl,
    areEqual,
}: {
    initialState: T;
    readState: (location: BrowserLocationSnapshot, fallback: T) => T;
    buildUrl: (state: T, location: BrowserLocationSnapshot) => string;
    areEqual: (left: T, right: T) => boolean;
}): {
    state: T;
    setState: HistoryBackedStateSetter<T>;
} {
    const [state, setStateInternal] = React.useState<T>(() => {
        if (typeof window === "undefined") {
            return initialState;
        }

        return readState(getLocationSnapshot(), initialState);
    });
    const latestStateRef = React.useRef(state);
    const readStateRef = React.useRef(readState);
    const buildUrlRef = React.useRef(buildUrl);
    const areEqualRef = React.useRef(areEqual);
    const previousInitialStateRef = React.useRef<T | null>(null);
    const skipNextNavigationRef = React.useRef(true);
    const navigationModeRef = React.useRef<"push" | HistoryReplaceReason>("push");
    const outgoingScrollRef = React.useRef<ScrollOffset | undefined>(undefined);
    /**
     * The page instance this hook belongs to, captured at mount.
     *
     * ⚠️ **A hook whose page instance has been superseded must not write the address — it would be
     * writing over somebody else's entry.** `beginPageInstance()` mints a fresh id on every enhanced
     * navigation (and at boot), so an id that no longer matches means this hook's page is on its way
     * out and a NEW page already owns the current history entry. Writing anyway replaces that entry's
     * url with one built from the outgoing page's state.
     *
     * That is not hypothetical, and it is the same ownership rule the pop verbs already enforce
     * (`popOwnGuardEntry` / `popOwnInPageEntries`) — this is the write side of it. Observed on devo
     * 2026-09-04 at the `5e1cf1ac9e` gate: an anonymous share into the Android shell navigates to the
     * composer, enhanced navigation pushes `/login`, and then the outgoing HOME page's canonicalize
     * effect replaced that entry with `/` — landing the member on the feed instead of the login page
     * they were sent to, roughly 1 run in 10 locally and 4 of 4 in CodeBuild. Nothing about the
     * rendered page looks wrong afterwards, which is why only a history-write trace found it.
     */
    const ownerPageInstanceRef = React.useRef<string>(
        typeof window === "undefined" ? "" : getPageInstanceId(),
    );

    /** False once another page instance owns the document; see `ownerPageInstanceRef`. */
    const stillOwnsTheAddress = React.useCallback(() => {
        if (typeof window === "undefined") {
            return false;
        }
        return getPageInstanceId() === ownerPageInstanceRef.current;
    }, []);

    latestStateRef.current = state;
    readStateRef.current = readState;
    buildUrlRef.current = buildUrl;
    areEqualRef.current = areEqual;

    const setState = React.useCallback<HistoryBackedStateSetter<T>>((action, options) => {
        navigationModeRef.current = options?.replace ?? "push";
        // Capture the offset the user is actually looking at, NOW — before React commits the new state.
        // The entry is written from a passive effect below, by which point a shorter destination has
        // already made the browser clamp `window.scrollY`; reading it there stamps a position the user
        // was never at, and Back restores that faithfully. See ScrollOffset in NavigationHistoryState.
        outgoingScrollRef.current = typeof window === "undefined"
            ? undefined
            : { scrollX: window.scrollX, scrollY: window.scrollY };

        setStateInternal((previous) => {
            const next = resolveStateAction(action, previous);
            return areEqualRef.current(previous, next) ? previous : next;
        });
    }, []);

    React.useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        const didInitialStateChange = previousInitialStateRef.current == null
            || !areEqualRef.current(previousInitialStateRef.current, initialState);

        previousInitialStateRef.current = initialState;

        if (!didInitialStateChange) {
            return;
        }

        const location = getLocationSnapshot();
        const parsed = readStateRef.current(location, initialState);
        const nextUrl = buildUrlRef.current(parsed, location);
        const currentUrl = getCurrentUrl(location);

        if (currentUrl !== nextUrl && stillOwnsTheAddress()) {
            // The mount-time normalization pass: the member is being moved off an address they never
            // asked for and onto its canonical form, so there is nothing behind it worth returning to.
            writeNavigationHistoryState("canonicalize", nextUrl);
        }

        if (!areEqualRef.current(latestStateRef.current, parsed)) {
            skipNextNavigationRef.current = true;
            latestStateRef.current = parsed;
            setStateInternal(parsed);
        }
    }, [initialState]);

    React.useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        if (skipNextNavigationRef.current) {
            skipNextNavigationRef.current = false;
            navigationModeRef.current = "push";
            // A pop or a normalization pass reached here, not the setter. Drop the captured offset so a
            // later push cannot stamp a stale one.
            outgoingScrollRef.current = undefined;
            return;
        }

        const location = getLocationSnapshot();
        const nextUrl = buildUrlRef.current(state, location);
        const currentUrl = getCurrentUrl(location);
        const outgoingScroll = outgoingScrollRef.current;
        outgoingScrollRef.current = undefined;

        if (currentUrl !== nextUrl && stillOwnsTheAddress()) {
            writeNavigationHistoryState(navigationModeRef.current, nextUrl, outgoingScroll);
        }

        navigationModeRef.current = "push";
    }, [state]);

    React.useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        const handlePopState = () => {
            const parsed = readStateRef.current(getLocationSnapshot(), initialState);
            if (areEqualRef.current(latestStateRef.current, parsed)) {
                return;
            }

            skipNextNavigationRef.current = true;
            latestStateRef.current = parsed;
            setStateInternal(parsed);
        };

        window.addEventListener("popstate", handlePopState);
        return () => window.removeEventListener("popstate", handlePopState);
    }, [initialState]);

    return { state, setState };
}
